/**
 * A web component that collapses rich text content to show only the first
 * block-level child (paragraph, list, etc.) with a "Read more" toggle button.
 *
 * Extra children are hidden via CSS by default (no layout shift).
 * JS only adds the toggle button and manages the expanded state.
 *
 * @example
 * <collapsible-text data-read-more="Read more" data-show-less="Show less">
 *   <div class="text-block rte">
 *     <p>First paragraph (visible when collapsed)</p>
 *     <p>Second paragraph (hidden when collapsed)</p>
 *   </div>
 * </collapsible-text>
 */
class CollapsibleText extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #buttonEl = null;

  connectedCallback() {
    const contentEl = this.firstElementChild;
    if (!contentEl) return;

    // If only one child, nothing to collapse — show everything
    if (contentEl.children.length <= 1) {
      this.dataset.expanded = '';
      return;
    }

    this.#createButton();
  }

  #createButton() {
    const readMore = this.dataset.readMore || 'Read more';
    const showLess = this.dataset.showLess || 'Show less';

    this.#buttonEl = document.createElement('button');
    this.#buttonEl.className = 'collapsible-text__toggle';
    this.#buttonEl.setAttribute('aria-expanded', 'false');
    this.#buttonEl.innerHTML = `<span class="collapsible-text__show">${readMore}</span><span class="collapsible-text__hide">${showLess}</span>`;
    this.#buttonEl.addEventListener('click', () => this.#toggle());
    this.appendChild(this.#buttonEl);
  }

  #toggle() {
    const expanding = !('expanded' in this.dataset);

    if (expanding) {
      this.dataset.expanded = '';
    } else {
      delete this.dataset.expanded;
    }

    this.#buttonEl?.setAttribute('aria-expanded', String(expanding));
  }
}

if (!customElements.get('collapsible-text')) {
  customElements.define('collapsible-text', CollapsibleText);
}
